import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  Reference,
  StoreObject,
  split,
  from,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { ReadFieldFunction } from '@apollo/client/cache/core/types/common';
import { FieldMergeFunction, FieldFunctionOptions } from '@apollo/client/cache/inmemory/policies';
import { setContext } from '@apollo/client/link/context';
import { UserInterface } from 'context/auth.context';
import { GetTemplateResponseLogDataQuery } from '__generated__/graphql';
import * as Sentry from '@sentry/react';

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_APP_GRAPHQL_API,
});

const wsLink = new GraphQLWsLink(
  createClient({
    url:
      import.meta.env.VITE_APP_GRAPHQL_API?.replace(/^http/, 'ws') || 'ws://localhost:3000/graphql',
    connectionParams: () => {
      let token = localStorage.getItem('restore.user');
      let tenantId;

      if (token) {
        const userJson: UserInterface = JSON.parse(token);
        token = userJson.tokens.id_token ?? userJson.tokens.access_token ?? null;
        tenantId = userJson.user.tenant?.id;
      }

      return {
        Authorization: token ? `Bearer ${token}` : '',
        tenant_id: tenantId,
      };
    },
    on: {
      error: (error) => console.error('WebSocket error:', error),
    },
  })
);

const authLink = setContext((_, { headers }) => {
  let token = localStorage.getItem('restore.user');
  let tenantId;

  if (token) {
    const userJson: UserInterface = JSON.parse(token);
    token = userJson.tokens.id_token ?? userJson.tokens.access_token ?? null;
    tenantId = userJson.user.tenant?.id;
  }

  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : '',
      Tenant_id: tenantId,
    },
  };
});

// Error link to capture GraphQL errors in Sentry
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      // Only send unexpected server errors to Sentry — not auth/permission denials
      const isServerError = extensions?.code === 'INTERNAL_SERVER_ERROR' || !extensions?.code; // Unknown errors

      if (isServerError) {
        Sentry.captureException(new Error(`GraphQL Error: ${message}`), {
          tags: {
            graphql: true,
            operation: operation.operationName,
          },
          contexts: {
            graphql: {
              message,
              locations,
              path,
              extensions,
            },
          },
        });
      }
    });
  }

  if (networkError) {
    // Only capture network errors that are server errors (5xx)
    const statusCode = (networkError as { statusCode?: number })?.statusCode;
    if (statusCode && statusCode >= 500) {
      Sentry.captureException(networkError, {
        tags: {
          graphql: true,
          networkError: true,
          operation: operation.operationName,
        },
        contexts: {
          network: {
            statusCode,
            message: networkError.message,
          },
        },
      });
    }
  }
});

// Split between HTTP and WebSocket links
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    const isSubscription =
      definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    // Debug: console.warn('🔀 Apollo link split - isSubscription:', isSubscription);
    return isSubscription;
  },
  wsLink,
  from([errorLink, authLink.concat(httpLink)])
);

// Type for mergeObjects function (not exported by Apollo Client)
type MergeObjectsFunction = <T extends StoreObject | Reference>(existing: T, incoming: T) => T;

// Helper function to merge existing and incoming arrays while preserving object references
const mergeExistingAndIncoming = ({
  existing,
  incoming,
  readField,
  mergeObjects,
}: {
  existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined;
  incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined;
  readField: ReadFieldFunction;
  mergeObjects: MergeObjectsFunction;
}): Array<StoreObject | Reference | undefined> => {
  const merged: Array<StoreObject | Reference | undefined> = incoming ? Array.from(incoming) : [];
  const recordToIndex: Record<string, number> = Object.create(null);

  if (incoming) {
    incoming.forEach((record, index) => {
      const recordId = readField<string | number>('id', record);
      if (recordId) recordToIndex[String(recordId)] = index;
    });
  }

  if (existing) {
    existing.forEach((record) => {
      const recordId = readField<string | number>('id', record);
      if (recordId) {
        const index = recordToIndex[String(recordId)];
        // This record exists within the incoming array -- combine the records
        if (typeof index === 'number') {
          const existingRecord = merged[index];
          if (existingRecord && record) {
            merged[index] = mergeObjects(
              existingRecord as StoreObject | Reference,
              record as StoreObject | Reference
            );
          }
        }
        // If the record does not exist within the incoming array, do nothing; it was removed from the list and should not be re-added
      }
    });
  }

  return merged;
};

// Helper to create pagination merge functions for fields with nested arrays
const makePaginationMerge = (
  resultsField: string
): FieldMergeFunction<Record<string, unknown>, Record<string, unknown>> => {
  return (existing, incoming, { readField }) => {
    const existingObj = existing as Record<string, unknown> | undefined;
    const incomingObj = incoming as Record<string, unknown>;

    const incomingArray = Array.isArray(incomingObj?.[resultsField])
      ? (incomingObj[resultsField] as Array<StoreObject | Reference | undefined>).slice()
      : [];

    const incomingIds = incomingArray.map((x) => readField<string | number>('id', x));

    const existingArray = Array.isArray(existingObj?.[resultsField])
      ? (existingObj[resultsField] as Array<StoreObject | Reference | undefined>)
      : [];

    const filteredExistingArray = existingArray.map((x) => {
      if (incomingIds.includes(readField<string | number>('id', x))) {
        const index = incomingArray.findIndex(
          (incomingItem) => readField('id', incomingItem) === readField('id', x)
        );
        if (index !== -1) {
          const value = incomingArray[index];
          incomingArray.splice(index, 1);
          return value;
        } else {
          return x;
        }
      }

      return x;
    });

    return {
      ...incomingObj,
      [resultsField]: [...filteredExistingArray, ...incomingArray],
    };
  };
};

export const apolloClient = new ApolloClient({
  connectToDevTools: import.meta.env.VITE_APP_ENVIRONMENT === 'development',
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          mediaPaginated: {
            keyArgs: ['input', ['tenantId', 'filters', 'sortBy']],
            merge: makePaginationMerge('stepResponseMediaItems'),
          },
          permissionsGrouped: {
            keyArgs: ['input', ['userGroupId', 'sortDirection']],
          },
          templateResponseLogData: {
            // Everything except dateRange here since that's used for pagination for Log view.
            keyArgs: ['input', ['tenantId', 'filters']],
            merge: (
              existing: GetTemplateResponseLogDataQuery['templateResponseLogData'] | undefined,
              incoming: GetTemplateResponseLogDataQuery['templateResponseLogData']
            ): GetTemplateResponseLogDataQuery['templateResponseLogData'] => {
              const existingStats = (existing?.stats ?? []).filter(
                (prevStat) =>
                  !incoming.stats.some(
                    (newStat) =>
                      newStat.storeId === prevStat.storeId &&
                      newStat.dateFor === prevStat.dateFor &&
                      newStat.status === prevStat.status
                  )
              );
              const dupedStats = (existing?.stats ?? []).filter(
                (prev) => !existingStats.includes(prev)
              );
              const dupedCount = dupedStats.reduce((sum, dupeStat) => {
                return sum + dupeStat.responseCount;
              }, 0);

              return {
                ...incoming,
                filteredCount:
                  (existing?.filteredCount ?? 0) + (incoming.filteredCount - dupedCount),
                stats: [...existingStats, ...incoming.stats],
              };
            },
          },
        },
      },
      UserGroup: {
        fields: {
          permissions: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
        },
      },
      Campaign: {
        fields: {
          stores: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
          tags: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
          users: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
        },
      },
      User: {
        fields: {
          tags: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
          userGroups: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
          brands: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
        },
      },
      Activity: {
        fields: {
          tags: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
          activityAssignments: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
          assignedUsers: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
          ccRecipients: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
          steps: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
          media: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
        },
      },
      Question: {
        fields: {
          questionChoices: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
        },
      },
      TagGroup: {
        fields: {
          tags: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
        },
      },
      Tenant: {
        fields: {
          userTagGroups: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
          storeTagGroups: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
        },
      },
      StepResponse: {
        fields: {
          stepResponseMedia: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
        },
      },
      Store: {
        fields: {
          brands: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
          users: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
          storeManagerUsers: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
          tags: {
            merge: (
              existing: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              incoming: ReadonlyArray<StoreObject | Reference | undefined> | undefined,
              { readField, mergeObjects }: FieldFunctionOptions
            ) => mergeExistingAndIncoming({ existing, incoming, readField, mergeObjects }),
          },
        },
      },
    },
  }),
});
